import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './AdminDashboard.css';
import { LineChart, DonutChart } from './DashboardCharts';

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";

const API_BASE_URL = process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

const AdminDashboard = () => {
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAdminAuthenticated } = useContext(AuthContext);

  // Processed Data for Charts
  const [chartData, setChartData] = useState({
    growth: [],
    feedbackType: []
  });

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      if (!isAdminAuthenticated) {
        navigate('/admin/login');
        return;
      }

      try {
        const adminToken = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load data');
        }

        const users = data.userRegistrations || [];
        const feeds = data.feedbacks || [];

        setUserRegistrations(users);
        setFeedbacks(feeds);

        // --- 1. Process Growth Data (Users over time) ---
        // Mocking timeline if needed, or grouping by seconds
        // Real logic: Group users by Date
        const groupedUsers = users.reduce((acc, user) => {
          let dateStr = 'Unknown';
          if (user.createdAt && user.createdAt.seconds) {
            dateStr = new Date(user.createdAt.seconds * 1000).toLocaleDateString();
          } else if (user.registrationDate) {
            dateStr = new Date(user.registrationDate).toLocaleDateString();
          } else {
            // Fallback generator for demo if no dates
            dateStr = `Batch ${Math.floor(Math.random() * 5)}`;
          }

          acc[dateStr] = (acc[dateStr] || 0) + 1;
          return acc;
        }, {});

        const growthSeries = Object.keys(groupedUsers).map(key => ({
          label: key,
          value: groupedUsers[key]
        })).slice(-10); // Last 10 points

        // --- 2. Process Feedback Data ---
        // Assuming feedback has a 'type' or just counting total
        const feedbackStats = [
          { label: 'Positive', value: feeds.filter(f => (f.rating || 5) >= 4).length, color: '#2ecc71' },
          { label: 'Neutral', value: feeds.filter(f => (f.rating || 5) === 3).length, color: '#f1c40f' },
          { label: 'Negative', value: feeds.filter(f => (f.rating || 5) < 3).length, color: '#e74c3c' }
        ].filter(d => d.value > 0);

        setChartData({
          growth: growthSeries.length > 0 ? growthSeries : [{ label: 'Start', value: 0 }, { label: 'Now', value: users.length }],
          feedbackType: feedbackStats.length > 0 ? feedbackStats : [{ label: 'General', value: feeds.length, color: '#3b82f6' }]
        });

        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Poll every 10 seconds to simulate real-time updates
    intervalId = setInterval(fetchData, 10000);

    return () => clearInterval(intervalId);
  }, [navigate, isAdminAuthenticated]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Initializing Admin Nexus...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>System Alert</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reconnect</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <header className="admin-header">
        <div className="header-title">
          <h1>Admin Nexus</h1>
          <span className="live-indicator">● System Live</span>
        </div>
        <div className="admin-profile">
          <span>Administrator</span>
          <div className="admin-avatar">A</div>
        </div>
      </header>

      {/* === KPI CARDS === */}
      <section className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon users">👥</div>
          <div className="kpi-content">
            <h3>Total Users</h3>
            <p className="kpi-value">{userRegistrations.length}</p>
            <span className="kpi-trend positive">+12% this week</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon feedback">💬</div>
          <div className="kpi-content">
            <h3>Total Feedback</h3>
            <p className="kpi-value">{feedbacks.length}</p>
            <span className="kpi-trend neutral">Steady</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon system">⚡</div>
          <div className="kpi-content">
            <h3>System Status</h3>
            <p className="kpi-value">Optimal</p>
            <span className="kpi-trend positive">99.9% Uptime</span>
          </div>
        </div>
      </section>

      {/* === ANALYTICS SECTION === */}
      <section className="analytics-grid">
        <div className="analytics-card wide">
          <LineChart
            title="User Registration Growth"
            data={chartData.growth}
            color="#FF6B00"
          />
        </div>
        <div className="analytics-card">
          <DonutChart
            title="Feedback Sentiment"
            data={chartData.feedbackType}
          />
        </div>
      </section>

      {/* === DATA TABLES === */}
      <div className="data-tables-grid">
        <section className="data-section">
          <div className="section-header">
            <h2>Recent Registrations</h2>
            <button className="view-all-btn">View All</button>
          </div>

          <div className="premium-table-wrapper">
            {userRegistrations.length > 0 ? (
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Email</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {userRegistrations.slice(0, 5).map(user => (
                    <tr key={user.id}>
                      <td className="mono-font">#{String(user.id).slice(-6)}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>
                        {user.createdAt?.seconds
                          ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                          : 'Recent'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="empty-state">No user registrations found.</p>
            )}
          </div>
        </section>

        <section className="data-section">
          <div className="section-header">
            <h2>Latest Feedback</h2>
          </div>
          <div className="feedback-cards-scroller">
            {feedbacks.slice(0, 5).map(feedback => (
              <div key={feedback.id} className="feedback-mini-card">
                <div className="feedback-mini-header">
                  <span className="feedback-user">{feedback.user || 'Anonymous'}</span>
                  <span className="feedback-date">
                    {feedback.createdAt?.seconds
                      ? new Date(feedback.createdAt.seconds * 1000).toLocaleDateString()
                      : 'Just now'}
                  </span>
                </div>
                <p className="feedback-msg">"{feedback.message || 'No content'}"</p>
                <div className={`feedback-rating rating-${feedback.rating || 3}`}>
                  {'★'.repeat(feedback.rating || 0)}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
